import { useCallback, useEffect, useRef, useState } from 'react';
import { DateRangeField } from '../components/DateRangeField';
import { cancelOtherSale, confirmOtherSale, createOtherSale, createOtherSaleSubcategory, deleteOtherSaleDraft, getOtherSale, getOtherSaleCosting, getOtherSaleInventoryTrace, getOtherSaleSubcategories, getOtherSaleSummary, getOtherSales, updateOtherSale } from '../api/otherSales';
import { getErrorMessage } from '../api/client';
import { getStores } from '../api/stores';
import { useAuth } from '../auth/AuthProvider';
import { canCancelOtherSale, canConfirmOtherSale, canCreateOtherSale, canCreateVoidRequest, canManageOtherSaleProducts, canViewCosts, getStoreScopeOptions } from '../auth/permissions';
import { Modal } from '../components/Modal';
import { DateTimeField } from '../components/DateTimeField';
import { Answers, ChoiceCards, FooterReason, Question } from '../components/capture';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { OtherSalePos } from '../components/OtherSalePos';
import { OtherSaleProductsAdmin } from '../components/OtherSaleProductsAdmin';
import { VoidRequestModal } from '../components/VoidRequestModal';
import { ClosedShiftNotice } from '../components/ClosedShiftNotice';
import type { CreateOtherSaleInput, InventoryTrace, LineTrace, OtherSaleCategory, OtherSaleDetail, OtherSaleListFilters, OtherSaleListItem, OtherSalePaymentMethod, OtherSalePersonRole, OtherSaleSortKey, OtherSaleStatus, OtherSaleSubcategory, OtherSaleSummary, OtherSaleSummaryFilters, OtherSaleTaxMode } from '../types/otherSales';
import type { Store } from '../types/stores';
import { formatMoney } from '../utils/costFormatting';
import { DEFAULT_STORE_TIMEZONE, dateToStoreISO } from '../utils/timezones';
import {
  OTHER_SALE_CATEGORY_LABELS,
  OTHER_SALE_PAYMENT_METHOD_LABELS,
  OTHER_SALE_STATUS_LABELS,
  OTHER_SALE_TAX_MODE_LABELS,
  getOtherSaleActions,
} from '../utils/otherSaleActions';
import { calculateOtherSaleTaxBreakdown } from '../utils/otherSaleTax';
import { formatResponseTaxMode } from '../utils/otherSaleProductForm';
import { displayFolio } from '../utils/otherSaleFolio';
import { consumeCatalogToken, otherSalesHashTarget, POS_HASH } from '../utils/newProductRoute';
import { notePosOpened } from '../utils/onboardingTelemetry';
import { describePeriod, deviceToday } from '../utils/periods';
import { FilterBar, FilterQuickSelect, SortSelect, type FilterChip } from '../components/filters/FilterBar';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionTabs, type SectionTab } from '../components/ui/SectionTabs';
import { SummaryStrip } from '../components/ui/SummaryStrip';
import { Icon } from '../components/ui/Icon';
import { RowToggle } from '../components/ui/RowToggle';
import { ActionBar } from '../components/ui/ActionBar';
import { useShellTier } from '../layouts/shellTier';
import { DenseList, DenseRow } from '../components/ui/DenseList';
import { MetaList } from '../components/ui/MetaList';
import { StatSummary } from '../components/ui/StatSummary';
import { Button, Field, LoadingPanel, Select } from '../components/ui/primitives';
import { consumptionStatusLabel, traceSummary } from '../utils/otherSaleInventoryTrace';
import { APPROXIMATE_COST_NOTE, costSummary, currentCostQuality, formatCostMoney } from '../utils/otherSaleCosting';
import { useLoyversePresence } from '../utils/loyversePresence';
import { getOtherSaleCostingReport, getCurrentProductCost, listOtherSaleProducts } from '../api/otherSales';
import { getUsers } from '../api/users';
import { WhoStack, type WhoEntry } from '../components/ui/WhoStack';
import { nextTableSort, sortChoices, type SortWording } from '../utils/tableSort';
import { userDisplayNameOr } from '../utils/userDisplayName';
import type { CostingReport, CurrentProductCost, LineCost, OtherSaleProduct, SaleCosting } from '../types/otherSales';

const PAGE_SIZE = 20;

/** Las vistas de Ventas e ingresos: el punto de venta, el historial, el catálogo y el costeo. */
type OtherSalesView = 'history' | 'pos' | 'catalog' | 'reports';

/** Cómo se lee cada columna al ordenar. Alimenta el selector de móvil y la etiqueta del orden actual. */
const SORT_WORDING: SortWording<OtherSaleSortKey> = {
  date: ['Fecha', 'más antigua primero', 'más reciente primero'],
  folio: ['Folio', 'del más bajo', 'del más alto'],
  store: ['Tienda', 'A–Z', 'Z–A'],
  // Estas tres ordenan por el valor CRUDO, que está en inglés, mientras la pantalla muestra la
  // etiqueta en español: «Estado» ascendente da Anulado, Confirmado, Borrador (CANCELLED, CONFIRMED,
  // DRAFT), que NO es alfabético en español. Por eso dicen «agrupado» y no «A–Z»: agrupar es lo que
  // de verdad hacen, y es el 90% de lo útil. Prometer un orden alfabético sería mentir.
  category: ['Categoría', 'agrupada', 'agrupada al revés'],
  subcategory: ['Subcategoría', 'A–Z', 'Z–A'],
  concept: ['Concepto', 'A–Z', 'Z–A'],
  // «Cliente» no está entre las opciones ofrecidas (la tabla no pinta esa columna), pero SÍ necesita
  // texto: se llega a ese orden desde el encabezado en escritorio y hay que saber decirlo.
  customer: ['Cliente', 'A–Z', 'Z–A'],
  total: ['Total', 'menor primero', 'mayor primero'],
  method: ['Método de pago', 'agrupado', 'agrupado al revés'],
  invoiced: ['Facturada', 'primero las no facturadas', 'primero las facturadas'],
  status: ['Estado', 'agrupado', 'agrupado al revés'],
  who: ['Quién la registró', 'A–Z', 'Z–A'],
};
/** Los órdenes que se ofrecen de un toque. «Cliente» queda fuera: la tabla no pinta esa columna. */
const SORT_CHOICES = [
  ['date', 'desc'], ['date', 'asc'], ['total', 'desc'], ['total', 'asc'],
  ['concept', 'asc'], ['store', 'asc'], ['category', 'asc'], ['method', 'asc'], ['status', 'asc'], ['who', 'asc'],
] as const;

/**
 * Quién registró la venta y quién la resolvió, para la columna «Quién». Gemelo del de Gastos: tres
 * papeles, porque una venta no pasa por aprobación. «Sin dato» lo pone WhoStack cuando el nombre
 * falta —las ventas resueltas antes de que se guardara la autoría no lo tienen— y nunca se inventa.
 */
function otherSaleWhoEntries(item: OtherSaleListItem): WhoEntry[] {
  const entries: WhoEntry[] = [{ label: 'Registró', name: item.createdByName }];
  if (item.status === 'CANCELLED') entries.push({ label: 'Anuló', name: item.cancelledByName });
  else if (item.status === 'CONFIRMED') entries.push({ label: 'Confirmó', name: item.confirmedByName });
  return entries;
}

/**
 * Los filtros del RESUMEN se derivan de los de la LISTA quitando lo que no cambia una suma: el orden
 * y la paginación. Derivarlos —en vez de escribirlos a mano— es lo que impide que los cinco totales
 * de arriba vuelvan a describir un conjunto distinto del que muestra la tabla.
 */
function summaryFiltersOf(filters: OtherSaleListFilters): OtherSaleSummaryFilters {
  const { sort: _sort, dir: _dir, limit: _limit, offset: _offset, ...rest } = filters;
  return rest;
}
const MIN_CANCEL_REASON_LENGTH = 10;
const CATEGORY_OPTIONS = Object.entries(OTHER_SALE_CATEGORY_LABELS) as Array<[OtherSaleCategory, string]>;
const TAX_MODE_OPTIONS = Object.entries(OTHER_SALE_TAX_MODE_LABELS) as Array<[OtherSaleTaxMode, string]>;
const PAYMENT_METHOD_OPTIONS = Object.entries(OTHER_SALE_PAYMENT_METHOD_LABELS) as Array<[OtherSalePaymentMethod, string]>;

export function OtherSalesPage() {
  const { user } = useAuth();
  const confirm = useConfirm();
  // ZTV-04: los avisos anti-duplicado con Loyverse solo aparecen si la integración existe.
  const loyverseConnected = useLoyversePresence();
  const [stores, setStores] = useState<Store[]>([]);
  // Deuda (m): «no hay tiendas» solo se puede afirmar cuando la consulta TERMINÓ Y BIEN. El error
  // va aparte del `error` general de la página, que lo comparten el historial y los borradores: si
  // no, un fallo del historial se pintaría sobre la pantalla de venta.
  const [storesLoaded, setStoresLoaded] = useState(false);
  const [storesFailed, setStoresFailed] = useState(false);
  /**
   * DOS estados de filtro, no uno. Con uno solo, cada select escribía directo en el filtro aplicado
   * y la paginación leía ese mismo objeto: cambiar «Categoría» y pulsar «Siguiente» sin aplicar
   * pedía la página con el filtro NUEVO y el desplazamiento VIEJO, saltándose ventas sin avisar.
   */
  const [draftFilters, setDraftFilters] = useState<OtherSaleListFilters>({});
  const [filters, setFilters] = useState<OtherSaleListFilters>({});
  /**
   * Personas para el filtro «Quién». Solo un dueño o gerente puede listar usuarios, así que a un
   * empleado la lista le llega vacía y el control no se dibuja: no es un permiso nuevo.
   */
  const [people, setPeople] = useState<Array<{ id: string; label: string }>>([]);
  /** Catálogo para el filtro EXACTO por producto (distinto de teclearlo en el buscador). */
  const [historyProducts, setHistoryProducts] = useState<OtherSaleProduct[]>([]);
  /** El texto que se está escribiendo en el buscador (se aplica al enviarlo). */
  const [searchText, setSearchText] = useState('');
  const [sort, setSort] = useState<OtherSaleSortKey>('date');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [items, setItems] = useState<OtherSaleListItem[]>([]);
  const [page, setPage] = useState({ total: 0, offset: 0, hasMore: false });
  const [summary, setSummary] = useState<OtherSaleSummary>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [formModalOpen, setFormModalOpen] = useState<'new' | string | undefined>();
  // Deep-link #other-sales/<id> ("Ver registro" desde Solicitudes): abre el detalle directo.
  // Bloque E (N8): solo un UUID es el id de una venta (lo que llevan «Ver registro» y Cortes).
  // `#other-sales/catalog` abre el catálogo, al que vuelve «Nuevo producto»; lo demás se ignora.
  const [detailId, setDetailIdState] = useState<string | undefined>(() => otherSalesHashTarget(window.location.hash).saleId);
  const setDetailId = (id: string | undefined) => { setDetailIdState(id); if (id === undefined && window.location.hash.startsWith('#other-sales/')) window.location.hash = 'other-sales'; };
  // Fase 21C-4A/4B: dentro del mismo módulo se alterna entre el historial de ventas, el punto de
  // venta (todo rol que pueda registrar ventas) y la administración del catálogo (OWNER/MANAGER).
  // P-075 (D-1, decisión del dueño): la sección abre su Historial; el punto de venta se abre con
  // «Vender», que lleva a `#other-sales/vender` (el punto de venta no se movió de ruta: es esta vista).
  // Un deep-link a un registro abre el historial; `#other-sales/catalog`, el catálogo.
  const [view, setView] = useState<OtherSalesView>(() => {
    const target = otherSalesHashTarget(window.location.hash);
    if (target.catalog && canManageOtherSaleProducts(user?.role)) return 'catalog';
    if (target.sell && canCreateOtherSale(user?.role)) return 'pos';
    return 'history';
  });
  const viewRef = useRef(view);
  viewRef.current = view;
  // P-085 (Codex R1, K3): «abrir el punto de venta» es ENTRAR a esta vista (desde el historial, el catálogo o un enlace
  // directo), no montar la sección. Solo el dueño; una vez por sesión del navegador y deduplicado en el servidor.
  useEffect(() => {
    if (view === 'pos' && user?.role === 'OWNER') notePosOpened(user.businessId);
  }, [view, user?.role, user?.businessId]);
  // El token del catálogo (o un segmento que no es nada) se consume al abrir: la URL vuelve a `#other-sales`.
  // El del punto de venta se queda mientras se vende (el menú resalta «Vender»).
  useEffect(() => { consumeCatalogToken(); }, []);
  // La URL manda: «Vender» desde el menú mientras se está aquí, o el Atrás del navegador entre «Vender» y
  // el resto de la sección, cambian la vista sin remontar la página.
  useEffect(() => {
    const followHash = () => {
      if (window.location.hash.replace('#', '').split('/')[0] !== 'other-sales') return;
      if (otherSalesHashTarget(window.location.hash).sell) {
        if (canCreateOtherSale(user?.role)) setView('pos');
      } else if (viewRef.current === 'pos') setView('history');
    };
    window.addEventListener('hashchange', followHash);
    return () => window.removeEventListener('hashchange', followHash);
  }, [user?.role]);
  /** Cambia de vista y deja la URL diciendo lo mismo: `#other-sales/vender` solo mientras se vende. */
  const showView = (next: OtherSalesView) => {
    setView(next);
    const sellingInUrl = otherSalesHashTarget(window.location.hash).sell;
    if (next === 'pos' && !sellingInUrl) window.location.hash = POS_HASH;
    else if (next !== 'pos' && sellingInUrl) window.location.hash = 'other-sales';
  };
  // Fase 21C-5: aviso compacto y descartable (se recuerda oculto durante la sesión) para no robar
  // espacio operativo en móvil. El banner global "Modo piloto" (AppLayout) es aparte.
  const [showTip, setShowTip] = useState(() => sessionStorage.getItem('otherSalesTipDismissed') !== '1');

  const load = useCallback(async (rawFilters: OtherSaleListFilters, offset = 0) => {
    setIsLoading(true);
    setError(undefined);
    // Un buscador con SOLO espacios llegaba al backend como texto vacío y su esquema exige al menos
    // un carácter: la pantalla se caía con «La información enviada no es válida» por haber pulsado
    // la barra espaciadora. Se normaliza aquí y no en el onChange: recortar mientras se teclea
    // impediría escribir el espacio entre dos palabras.
    const buscado = rawFilters.search?.trim();
    // `sort` y `dir` solo viajan si NO son los de por defecto: los esquemas del backend son
    // `.strict()` y un servidor anterior los rechazaría con 400 durante la ventana de despliegue.
    const ordenPorDefecto = (rawFilters.sort ?? 'date') === 'date' && (rawFilters.dir ?? 'desc') === 'desc';
    const nextFilters: OtherSaleListFilters = {
      ...rawFilters,
      search: buscado || undefined,
      sort: ordenPorDefecto ? undefined : rawFilters.sort,
      dir: ordenPorDefecto ? undefined : rawFilters.dir,
    };
    try {
      const [listResult, summaryResult] = await Promise.all([
        getOtherSales({ ...nextFilters, limit: PAGE_SIZE, offset }),
        // Las MISMAS condiciones que la lista. Antes solo se le pasaban tienda, categoría y fechas,
        // así que filtrar por método de pago o por «facturada» —dos controles que la barra YA
        // ofrecía— no movía ninguno de los cinco totales. Ese defecto estaba en producción.
        getOtherSaleSummary(summaryFiltersOf(nextFilters)),
      ]);
      setFilters(nextFilters);
      setItems(listResult.items);
      setPage({ total: listResult.total, offset: listResult.offset, hasMore: listResult.hasMore });
      setSummary(summaryResult);
    } catch (loadError) {
      setError(getErrorMessage(loadError, 'No fue posible cargar Ventas Otros.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void getStores().then((result) => {
      setStores(result.stores);
      const { defaultStoreId } = getStoreScopeOptions(user?.role, result.stores);
      const initialFilters: OtherSaleListFilters = defaultStoreId ? { storeId: defaultStoreId } : {};
      setDraftFilters(initialFilters);
      void load(initialFilters);
    }).catch((loadError) => {
      setStoresFailed(true);
      setError(getErrorMessage(loadError, 'No fue posible cargar las tiendas.'));
    }).finally(() => setStoresLoaded(true));
    // Personas y productos van aparte y con su propio catch: que un empleado no pueda listar
    // usuarios no debe impedirle ver sus ventas. Sin lista, el filtro simplemente no se dibuja.
    void getUsers()
      .then((result) => setPeople(result.users.filter((person) => person.isActive).map((person) => ({ id: person.id, label: userDisplayNameOr(person) }))))
      .catch(() => setPeople([]));
    void listOtherSaleProducts({ includeInactive: true })
      .then((result) => setHistoryProducts(result.items))
      .catch(() => setHistoryProducts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Si el dueño acotó fechas a mano, esta es la salida visible cuando no encuentra lo que busca. */
  const hasDateWindow = Boolean(filters.dateFrom || filters.dateTo);
  /**
   * La tienda que el ROL impone. Un gerente o un empleado no pueden ver «todas»: el selector no les
   * ofrece la opción vacía y el backend responde 400 sin ella. No es un filtro que el dueño haya
   * puesto, así que ni «Limpiar» debe borrarla ni cuenta como filtro activo — si contara, el panel
   * vacío acusaría a un filtro inexistente el día que una sucursal aún no tiene ventas.
   */
  const { allowsAllStores, defaultStoreId: forcedStoreId } = getStoreScopeOptions(user?.role, stores);
  const baseFilters: OtherSaleListFilters = forcedStoreId ? { storeId: forcedStoreId } : {};
  const storeChosenByUser = Boolean(filters.storeId) && filters.storeId !== forcedStoreId;
  const hasActiveFilters = Boolean(filters.dateFrom || filters.dateTo || storeChosenByUser || filters.category
    || filters.status || filters.paymentMethod || filters.isInvoiced !== undefined || filters.search
    || filters.personId || filters.productId);
  const clearFilters = () => {
    // Vuelve al estado con el que ARRANCA la pantalla, que para un gerente o un empleado incluye su
    // tienda: mandar un objeto vacío les dejaría el selector en blanco y la pantalla en error 400.
    const limpio: OtherSaleListFilters = { ...baseFilters };
    setDraftFilters(limpio);
    setSearchText('');
    setSort('date');
    setDir('desc');
    void load(limpio, 0);
  };
  /** Repite la MISMA búsqueda sin el rango de fechas: el caso «sé que existe y no aparece». */
  const searchWholeHistory = () => {
    void load({ ...filters, sort, dir, dateFrom: undefined, dateTo: undefined }, 0);
  };

  /**
   * P-075 · La barra de filtros. Lo aplicado vive en `filters`. La búsqueda, el periodo y cada «×» se
   * aplican SOBRE lo aplicado: nunca arrastran un control que todavía está en borrador. Los demás filtros
   * se eligen en borrador en «Filtros» (`draftFilters`, que se copia de lo aplicado al abrirlo) y se piden
   * con «Ver resultados». El orden se conserva siempre.
   */
  const applyPatch = (patch: Partial<OtherSaleListFilters>) => { if (!isLoading) void load({ ...filters, ...patch, sort, dir }, 0); };
  const panelCount = (storeChosenByUser ? 1 : 0) + (filters.category ? 1 : 0) + (filters.status ? 1 : 0) + (filters.paymentMethod ? 1 : 0)
    + (filters.isInvoiced !== undefined ? 1 : 0) + (filters.productId ? 1 : 0) + (filters.personId ? 1 : 0);
  const storeNameOf = (id?: string) => stores.find((store) => store.id === id)?.name ?? 'elegida';
  const chips: FilterChip[] = [
    ...(filters.search ? [{ id: 'search', label: `Búsqueda: «${filters.search}»`, onRemove: () => { setSearchText(''); applyPatch({ search: undefined }); } }] : []),
    ...(hasDateWindow ? [{ id: 'dates', label: `Fechas: ${describePeriod({ from: filters.dateFrom ?? '', to: filters.dateTo ?? '' }, deviceToday())}`, onRemove: () => applyPatch({ dateFrom: undefined, dateTo: undefined }) }] : []),
    ...(storeChosenByUser ? [{ id: 'store', label: `Tienda: ${storeNameOf(filters.storeId)}`, onRemove: () => applyPatch({ storeId: forcedStoreId }) }] : []),
    ...(filters.category ? [{ id: 'category', label: `Categoría: ${OTHER_SALE_CATEGORY_LABELS[filters.category]}`, onRemove: () => applyPatch({ category: undefined }) }] : []),
    ...(filters.status ? [{ id: 'status', label: `Estado: ${OTHER_SALE_STATUS_LABELS[filters.status]}`, onRemove: () => applyPatch({ status: undefined }) }] : []),
    ...(filters.paymentMethod ? [{ id: 'method', label: `Pago: ${OTHER_SALE_PAYMENT_METHOD_LABELS[filters.paymentMethod]}`, onRemove: () => applyPatch({ paymentMethod: undefined }) }] : []),
    ...(filters.isInvoiced !== undefined ? [{ id: 'invoiced', label: filters.isInvoiced ? 'Facturadas' : 'No facturadas', onRemove: () => applyPatch({ isInvoiced: undefined }) }] : []),
    ...(filters.productId ? [{ id: 'product', label: `Producto: ${historyProducts.find((product) => product.id === filters.productId)?.name ?? 'elegido'}`, onRemove: () => applyPatch({ productId: undefined }) }] : []),
    ...(filters.personId ? [{ id: 'person', label: `Quién: ${people.find((person) => person.id === filters.personId)?.label ?? 'elegida'}`, onRemove: () => applyPatch({ personId: undefined, personRole: undefined }) }] : []),
  ];

  /**
   * El orden vive en el SERVIDOR. La lista pide 20 filas por página: ordenar aquí reordenaría solo
   * lo visible y diría «la venta más grande» cuando es «la más grande de estas veinte». Cada clic
   * vuelve a la primera página, porque reordenar estando en la tercera dejaría al dueño mirando un
   * tramo arbitrario del orden nuevo.
   */
  const sortBy = (key: OtherSaleSortKey) => {
    const next = nextTableSort({ key: sort, dir }, key);
    setSort(next.key);
    setDir(next.dir);
    void load({ ...filters, sort: next.key, dir: next.dir }, 0);
  };
  const th = (key: OtherSaleSortKey, label: string) => {
    const active = sort === key;
    return <th aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}>
      <button className="data-table__sort" onClick={() => sortBy(key)} type="button">{label}
        <span aria-hidden="true" className={active ? 'data-table__sort-arrow data-table__sort-arrow--active' : 'data-table__sort-arrow'}>{active ? (dir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>;
  };

  const refreshAfterChange = async (message: string) => {
    setNotice(message);
    await load(filters, page.offset);
  };

  const handleDeleteDraft = async (sale: Pick<OtherSaleListItem, 'id'>) => {
    if (!(await confirm({ title: 'Eliminar borrador', description: 'Esta acción no afecta reportes confirmados. ¿Eliminar este borrador?', confirmLabel: 'Eliminar', danger: true }))) return;
    setError(undefined);
    try {
      await deleteOtherSaleDraft(sale.id);
      if (detailId === sale.id) setDetailId(undefined);
      await refreshAfterChange('Borrador eliminado.');
    } catch (deleteError) {
      setError(getErrorMessage(deleteError, 'No fue posible eliminar el borrador.'));
    }
  };

  const handleConfirm = async (sale: Pick<OtherSaleListItem, 'id'>) => {
    setError(undefined);
    try {
      await confirmOtherSale(sale.id);
      await refreshAfterChange('Venta confirmada correctamente.');
    } catch (confirmError) {
      setError(getErrorMessage(confirmError, 'No fue posible confirmar la venta.'));
    }
  };

  /**
   * Rediseño · Bloque D. Mientras se vende, la página NO dibuja su encabezado, su aviso ni sus pestañas:
   * el punto de venta ofrece esas mismas salidas con la suya, bajo las mismas condiciones, una por una.
   */
  const selling = view === 'pos';
  const tipVisible = showTip && loyverseConnected === true;
  const dismissTip = () => { sessionStorage.setItem('otherSalesTipDismissed', '1'); setShowTip(false); };
  const posViewLinks = [
    { id: 'history', label: 'Historial', onOpen: () => showView('history') },
    ...(canManageOtherSaleProducts(user?.role) ? [{ id: 'catalog', label: 'Catálogo de productos', onOpen: () => showView('catalog') }] : []),
    ...(canViewCosts(user?.role) ? [{ id: 'reports', label: 'Consumo y margen', onOpen: () => showView('reports') }] : []),
  ];
  // P-075: un solo nombre para el punto de venta en toda la app: «Vender» (antes «Venta rápida»).
  const viewTabs: SectionTab[] = [
    ...(canCreateOtherSale(user?.role) ? [{ id: 'pos', label: 'Vender' }] : []),
    { id: 'history', label: 'Historial' },
    ...(canManageOtherSaleProducts(user?.role) ? [{ id: 'catalog', label: 'Catálogo de productos' }] : []),
    ...(canViewCosts(user?.role) ? [{ id: 'reports', label: 'Consumo y margen' }] : []),
  ];
  const selectOptions = <T extends string>(options: Array<[T, string]>) => options.map(([value, label]) => <option key={value} value={value}>{label}</option>);
  const panelFields = <>
    <Field label="Tienda">
      <Select onChange={(event) => setDraftFilters({ ...draftFilters, storeId: event.target.value || undefined })} value={draftFilters.storeId || ''}>
        {allowsAllStores && <option value="">Todas las tiendas</option>}
        {stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}
      </Select>
    </Field>
    <Field label="Categoría">
      <Select onChange={(event) => setDraftFilters({ ...draftFilters, category: (event.target.value || undefined) as OtherSaleCategory | undefined })} value={draftFilters.category || ''}>
        <option value="">Todas</option>{selectOptions(CATEGORY_OPTIONS)}
      </Select>
    </Field>
    <Field label="Estado">
      <Select onChange={(event) => setDraftFilters({ ...draftFilters, status: (event.target.value || undefined) as OtherSaleStatus | undefined })} value={draftFilters.status || ''}>
        <option value="">Todos</option><option value="DRAFT">Borrador</option><option value="CONFIRMED">Confirmado</option><option value="CANCELLED">Anulado</option>
      </Select>
    </Field>
    <Field label="Método de pago">
      <Select onChange={(event) => setDraftFilters({ ...draftFilters, paymentMethod: (event.target.value || undefined) as OtherSalePaymentMethod | undefined })} value={draftFilters.paymentMethod || ''}>
        <option value="">Todos</option>{selectOptions(PAYMENT_METHOD_OPTIONS)}
      </Select>
    </Field>
    <Field label="Facturada">
      <Select onChange={(event) => setDraftFilters({ ...draftFilters, isInvoiced: event.target.value === '' ? undefined : event.target.value === 'true' })} value={draftFilters.isInvoiced === undefined ? '' : String(draftFilters.isInvoiced)}>
        <option value="">Todas</option><option value="true">Facturadas</option><option value="false">No facturadas</option>
      </Select>
    </Field>
    {/* Filtro EXACTO, distinto de teclearlo en el buscador: «paleta» encuentra la de fresa y la de limón;
        esto deja elegir una sola. */}
    {historyProducts.length > 0 && <Field label="Producto">
      <Select onChange={(event) => setDraftFilters({ ...draftFilters, productId: event.target.value || undefined })} value={draftFilters.productId || ''}>
        <option value="">Todos</option>
        {historyProducts.map((product) => <option key={product.id} value={product.id}>{product.name}</option>)}
      </Select>
    </Field>}
    {people.length > 0 && <Field label="Quién">
      <Select onChange={(event) => setDraftFilters({ ...draftFilters, personId: event.target.value || undefined })} value={draftFilters.personId || ''}>
        <option value="">Cualquiera</option>
        {people.map((person) => <option key={person.id} value={person.id}>{person.label}</option>)}
      </Select>
    </Field>}
    {/* Tres papeles, no cuatro: una venta no pasa por aprobación, así que no hay «la envió». */}
    {people.length > 0 && draftFilters.personId && <Field label="En qué papel">
      <Select onChange={(event) => setDraftFilters({ ...draftFilters, personRole: event.target.value as OtherSalePersonRole })} value={draftFilters.personRole || 'any'}>
        <option value="any">Cualquier papel</option>
        <option value="created">La registró</option>
        <option value="confirmed">La confirmó</option>
        <option value="cancelled">La anuló</option>
      </Select>
    </Field>}
  </>;

  return <div className={selling ? 'other-sales-page' : 'n3-screen other-sales-page'}>
    {!selling && <PageHeader
      actions={view === 'history' && canCreateOtherSale(user?.role)
        ? <Button iconStart={<Icon name="plus" size={18} />} onClick={() => setFormModalOpen('new')} variant="secondary">Registrar ingreso manual</Button>
        : undefined}
      description={loyverseConnected === true
        ? 'Lo que cobraste con el punto de venta y los ingresos que capturaste a mano. Las ventas de Loyverse están en Ventas Loyverse.'
        : 'Lo que cobraste con el punto de venta y los ingresos que capturaste a mano.'}
      title="Ventas e ingresos"
    />}
    {/* ZTV-04: el aviso anti-duplicado solo tiene sentido cuando existe la integración Loyverse. */}
    {!selling && tipVisible && <div className="alert alert--warning other-sales-tip">
      <span>Registra aquí solo ventas que <strong>no</strong> estén en Loyverse (para no duplicar).</span>
      <button aria-label="Ocultar aviso" className="icon-button" onClick={dismissTip} type="button">×</button>
    </div>}
    {/* Pestañas en orden operación → consulta → análisis. «Vender» nunca se ve activa: mientras se vende,
        esta fila no se dibuja (Bloque D). */}
    {!selling && <SectionTabs active={view} label="Vistas de Ventas e ingresos" onSelect={(id) => showView(id as OtherSalesView)} tabs={viewTabs} />}

    {view === 'catalog' ? <OtherSaleProductsAdmin stores={stores} />
      : view === 'reports' ? <CostingReportView stores={stores} />
      : view === 'pos' ? <OtherSalePos stores={stores} storesLoaded={storesLoaded} storesFailed={storesFailed} onSaleCreated={() => void load(filters, 0)} onOpenDetail={(saleId) => { showView('history'); setDetailId(saleId); }} onGoToHistory={() => { showView('history'); void load(filters, 0); }} onGoToCatalog={() => showView('catalog')} viewLinks={posViewLinks} loyverseTip={tipVisible ? { onDismiss: dismissTip } : undefined} />
      : <>
    {error && <div className="alert alert--error">{error}</div>}
    {notice && <div className="alert alert--success">{notice}</div>}

    {/* Una cifra a la vista y el resto en el desglose (las mismas cinco, con los mismos filtros que la lista). */}
    {summary && <SummaryStrip
      details={[
        { label: 'Subtotal (confirmado)', value: formatMoney(summary.subtotalAmount) },
        { label: 'IVA (confirmado)', value: formatMoney(summary.taxAmount) },
        { label: 'Total borrador', value: formatMoney(summary.draftTotal) },
        { label: 'Total anulado', value: formatMoney(summary.cancelledTotal), muted: true },
      ]}
      label="Totales de Ventas e ingresos"
      primary={{ label: 'Total confirmado', value: formatMoney(summary.confirmedTotal) }}
    />}

    {/* Busca también DENTRO de los renglones del punto de venta, por el nombre que el producto tenía al
        venderse. maxLength = el mismo tope que el esquema del backend (120): así PEGAR un texto largo no
        puede devolver 400. */}
    <FilterBar
      busy={isLoading}
      chips={chips}
      label="Buscar y filtrar ventas e ingresos"
      onClear={hasActiveFilters ? clearFilters : undefined}
      panel={{
        applying: isLoading,
        content: panelFields,
        count: panelCount,
        onApply: () => { if (!isLoading) void load({ ...draftFilters, search: filters.search, dateFrom: filters.dateFrom, dateTo: filters.dateTo, sort, dir }, 0); },
        onOpen: () => setDraftFilters({ ...filters }),
        onReset: () => setDraftFilters({ ...baseFilters }),
      }}
      period={<DateRangeField disabled={isLoading} from={filters.dateFrom || ''} onChange={(from, to) => applyPatch({ dateFrom: from || undefined, dateTo: to || undefined })} to={filters.dateTo || ''} />}
      quick={<>
        {stores.length > 1 && <FilterQuickSelect disabled={isLoading} label="Tienda" onChange={(value) => applyPatch({ storeId: value || undefined })} options={[...(allowsAllStores ? [{ value: '', label: 'Todas las tiendas' }] : []), ...stores.map((store) => ({ value: store.id, label: store.name }))]} value={filters.storeId || ''} />}
        <FilterQuickSelect disabled={isLoading} label="Estado" onChange={(value) => applyPatch({ status: (value || undefined) as OtherSaleStatus | undefined })} options={[{ value: '', label: 'Todos' }, { value: 'DRAFT', label: 'Borrador' }, { value: 'CONFIRMED', label: 'Confirmado' }, { value: 'CANCELLED', label: 'Anulado' }]} value={filters.status || ''} />
      </>}
      search={{ label: 'Buscar ventas e ingresos', maxLength: 120, onChange: setSearchText, onSubmit: (text) => applyPatch({ search: text || undefined }), placeholder: 'Folio, producto, cliente o nota', value: searchText }}
      sort={<SortSelect
        disabled={isLoading}
        onChange={(value) => { const [key, nextDir] = value.split(':') as [OtherSaleSortKey, 'asc' | 'desc']; setSort(key); setDir(nextDir); void load({ ...filters, sort: key, dir: nextDir }, 0); }}
        options={sortChoices(SORT_WORDING, SORT_CHOICES, { key: sort, dir })}
        value={`${sort}:${dir}`}
      />}
      summary={`${page.total} ${page.total === 1 ? 'registro' : 'registros'}${hasActiveFilters ? ' en lo que estás viendo' : ''}`}
    />

    {isLoading ? <LoadingPanel label="Cargando ventas e ingresos…" /> : <OtherSalesTable items={items} onOpen={setDetailId} onConfirm={handleConfirm} onDeleteDraft={handleDeleteDraft} stores={stores} th={th} hasActiveFilters={hasActiveFilters} hasDateWindow={hasDateWindow} onClearFilters={clearFilters} onSearchWholeHistory={searchWholeHistory} />}
    {!isLoading && page.total > 0 && <div className="coverage-pagination">
      <span>Mostrando {page.offset + 1}–{page.offset + items.length} de {page.total}</span>
      <div>
        <button className="button button--ghost" disabled={page.offset === 0} onClick={() => void load(filters, Math.max(0, page.offset - PAGE_SIZE))} type="button">Anterior</button>
        <button className="button button--ghost" disabled={!page.hasMore} onClick={() => void load(filters, page.offset + PAGE_SIZE)} type="button">Siguiente</button>
      </div>
    </div>}

    {formModalOpen && <OtherSaleFormModal
      saleId={formModalOpen === 'new' ? undefined : formModalOpen}
      stores={stores}
      onClose={() => setFormModalOpen(undefined)}
      onSaved={async (message) => { setFormModalOpen(undefined); await refreshAfterChange(message); }}
    />}

    {detailId && <OtherSaleDetailModal
      saleId={detailId}
      stores={stores}
      onClose={() => setDetailId(undefined)}
      onEdit={() => setFormModalOpen(detailId)}
      onDeleteDraft={() => handleDeleteDraft({ id: detailId })}
      onChanged={async (message) => { setDetailId(undefined); await refreshAfterChange(message); }}
    />}
    </>}
  </div>;
}

function OtherSalesTable({ items, onOpen, onConfirm, onDeleteDraft, stores, th, hasActiveFilters, hasDateWindow, onClearFilters, onSearchWholeHistory }: {
  items: OtherSaleListItem[];
  onOpen: (id: string) => void;
  onConfirm: (sale: Pick<OtherSaleListItem, 'id'>) => Promise<void>;
  onDeleteDraft: (sale: Pick<OtherSaleListItem, 'id'>) => Promise<void>;
  stores: Store[];
  th: (key: OtherSaleSortKey, label: string) => JSX.Element;
  hasActiveFilters: boolean;
  hasDateWindow: boolean;
  onClearFilters: () => void;
  onSearchWholeHistory: () => void;
}) {
  const { user } = useAuth();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) => setExpandedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  // Antes decía «Sin ventas registradas» aunque hubiera cientos y lo único que pasara es que
  // ninguna casara con el filtro: el dueño no sabía si el dato no existe o si su filtro lo esconde.
  if (!items.length) return <div className="empty-panel">{hasActiveFilters
    ? <><strong>Ninguna venta coincide con lo que buscas</strong><span>{hasDateWindow ? 'La pantalla está mirando solo un rango de fechas. Puede que la venta exista fuera de él.' : 'Prueba con otro texto, o quita algún filtro.'}</span><div className="empty-panel__actions">{hasDateWindow && <button className="button button--secondary" onClick={onSearchWholeHistory} type="button">Buscar en todo el historial</button>}<button className="text-button" onClick={onClearFilters} type="button">Quitar todos los filtros</button></div></>
    : <><strong>Sin ventas registradas</strong><span>Registra tu primera venta para comenzar.</span></>}</div>;
  // Móvil (≤640px): data-table--cards convierte cada venta en tarjeta colapsable (mismo patrón que cortes).
  return <div className="data-table-wrap"><table className="data-table other-sales-table other-sales-table--who data-table--cards n3-has-row-toggle"><thead><tr>{th('date', 'Folio · Fecha')}{th('store', 'Tienda')}{th('category', 'Categoría')}{th('subcategory', 'Subcategoría')}{th('concept', 'Concepto')}{/* «Monto» e «IVA» NO se ordenan: se leen de subtotalAmount y taxAmount, que pueden venir vacíos, y la pantalla los sustituye a la vista por el total y por 0.00 — ordenar por ellas ordenaría por una clave distinta de la que se está leyendo. «Total» sí, y es la que de verdad se mira. */}<th>Monto</th><th>IVA</th>{th('total', 'Total')}{th('method', 'Método de pago')}{th('invoiced', 'Facturada')}{th('status', 'Estado')}{th('who', 'Quién')}<th>Acciones</th></tr></thead><tbody>{items.map((item) => {
    const actions = getOtherSaleActions(item.status);
    const timeZone = stores.find((store) => store.id === item.storeId)?.timezone ?? DEFAULT_STORE_TIMEZONE;
    return <tr className={`${item.status === 'CANCELLED' ? 'data-row--inactive' : item.hasPendingVoidRequest || item.hasApprovedChangeRequest ? 'data-row--flagged' : ''} ${expandedIds.has(item.id) ? 'dt-card--open' : 'dt-card--closed'}`} onClick={() => toggleExpanded(item.id)} key={item.id}>
      <td className="dt-card-main"><strong><span className="folio-tag">{displayFolio(item.folio)}</span> · {formatSaleDate(item.saleDate, timeZone)}</strong><small>{item.storeName || 'Ingreso general'}</small><RowToggle expanded={expandedIds.has(item.id)} label={item.folio || item.concept} onToggle={() => toggleExpanded(item.id)} /></td>
      <td data-label="Tienda">{item.storeName || 'Ingreso general'}</td>
      <td data-label="Categoría">{OTHER_SALE_CATEGORY_LABELS[item.category]}</td>
      <td data-label="Subcategoría">{item.subcategoryName || '—'}</td>
      <td className="dt-card-keep" data-label="Concepto">{item.concept}</td>
      <td data-label="Monto">{item.subtotalAmount ? formatMoney(item.subtotalAmount, item.currency) : formatMoney(item.amount, item.currency)}</td>
      <td data-label="IVA">{item.taxAmount ? formatMoney(item.taxAmount, item.currency) : formatMoney('0.00', item.currency)}</td>
      <td className="dt-card-keep" data-label="Total"><strong>{formatMoney(item.amount, item.currency)}</strong></td>
      <td data-label="Método de pago">{item.paymentMethod ? OTHER_SALE_PAYMENT_METHOD_LABELS[item.paymentMethod] : 'Sin especificar'}</td>
      <td data-label="Facturada">{item.isInvoiced ? 'Sí' : 'No'}</td>
      <td className="dt-card-keep" data-label="Estado"><OtherSaleStatusPill status={item.status} />{item.status !== 'CANCELLED' && item.hasPendingVoidRequest && <span className="consumption-status consumption-status--pending">Solicitud pendiente</span>}{item.status !== 'CANCELLED' && item.hasApprovedChangeRequest && <span className="consumption-status consumption-status--pending">Cambio aprobado — aplicar</span>}</td>
      {/* Los tres nombres YA viajaban en cada venta y solo se veían dentro del detalle. Mismo
          componente y mismo vocabulario que Compras y Gastos, para no llamar a lo mismo de dos
          maneras. dt-card-keep: en móvil se ve también con la tarjeta colapsada. */}
      <td className="dt-card-keep who-cell" data-label="Quién"><WhoStack entries={otherSaleWhoEntries(item)} /></td>
      <td className="dt-card-actions dt-card-keep"><div className="table-actions">
        <button className="text-button" onClick={(event) => { event.stopPropagation(); onOpen(item.id); }} type="button">Ver detalle</button>
        {actions.canConfirm && canConfirmOtherSale(user?.role) && <button className="text-button" onClick={(event) => { event.stopPropagation(); void onConfirm(item); }} type="button">Confirmar</button>}
        {actions.canDeleteDraft && <button className="text-button text-button--danger" onClick={(event) => { event.stopPropagation(); void onDeleteDraft(item); }} type="button">Eliminar</button>}
      </div></td>
    </tr>;
  })}</tbody></table></div>;
}

function OtherSaleDetailModal({ saleId, stores, onClose, onEdit, onDeleteDraft, onChanged }: {
  saleId: string;
  stores: Store[];
  onClose: () => void;
  onEdit: () => void;
  onDeleteDraft: () => Promise<void>;
  onChanged: (message: string) => Promise<void>;
}) {
  const { user } = useAuth();
  // Codex R2 · H3: las filas compactas solo en el teléfono; en tableta y escritorio, las tablas de siempre.
  const tier = useShellTier();
  const [detail, setDetail] = useState<OtherSaleDetail>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isVoidRequestModalOpen, setIsVoidRequestModalOpen] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(undefined);
    try {
      const result = await getOtherSale(saleId);
      setDetail(result.otherSale);
    } catch (loadError) {
      setError(getErrorMessage(loadError, 'No fue posible cargar el detalle de la venta.'));
    } finally {
      setIsLoading(false);
    }
  }, [saleId]);

  useEffect(() => { void load(); }, [load]);

  if (!detail && isLoading) return <Modal title="Detalle de la venta" onClose={onClose}><div className="inline-loading"><span className="spinner" />Cargando detalle...</div></Modal>;
  if (!detail) return <Modal title="Detalle de la venta" onClose={onClose}>{error && <div className="alert alert--error">{error}</div>}</Modal>;

  const actions = getOtherSaleActions(detail.status);
  const timeZone = stores.find((store) => store.id === detail.storeId)?.timezone ?? DEFAULT_STORE_TIMEZONE;

  return <Modal description={`${displayFolio(detail.folio)} · ${formatSaleDate(detail.saleDate, timeZone)} · ${detail.storeName || 'Ingreso general'}`} title="Detalle de la venta" onClose={onClose}>
    {error && <div className="alert alert--error">{error}</div>}
    {/* P-076: primero lo que se viene a ver (total, estado, método, factura); después cada dato con su
        etiqueta, en lista. Son los mismos 19 datos de antes; folio, fecha y tienda van además en la descripción. */}
    <StatSummary
      label="Resumen de la venta"
      stats={[
        { label: 'Total', value: formatMoney(detail.amount, detail.currency) },
        { label: 'Estado', value: <OtherSaleStatusPill status={detail.status} /> },
        { label: 'Método de pago', value: detail.paymentMethod ? OTHER_SALE_PAYMENT_METHOD_LABELS[detail.paymentMethod] : 'Sin especificar' },
        { label: '¿Facturada?', value: detail.isInvoiced ? 'Sí' : 'No' },
      ]}
    />
    <MetaList
      aria-label="Datos de la venta"
      columns
      items={[
        { label: 'Concepto', value: detail.concept, emphasis: true },
        { label: 'Categoría', value: OTHER_SALE_CATEGORY_LABELS[detail.category] },
        ...(detail.subcategoryName ? [{ label: 'Subcategoría', value: detail.subcategoryName }] : []),
        { label: 'Fecha', value: formatSaleDate(detail.saleDate, timeZone) },
        { label: 'Tienda', value: detail.storeName || 'Ingreso general' },
        { label: 'Subtotal', value: detail.subtotalAmount ? formatMoney(detail.subtotalAmount, detail.currency) : formatMoney(detail.amount, detail.currency) },
        { label: `IVA (${detail.taxMode === 'MIXED' ? 'mixto' : `${detail.taxPercentage ? Number(detail.taxPercentage).toFixed(1) : '0.0'}%`})`, value: detail.taxAmount ? formatMoney(detail.taxAmount, detail.currency) : formatMoney('0.00', detail.currency) },
        { label: 'Cliente', value: detail.customerName || 'Sin cliente' },
        // Fase 22A: campo informativo capturado a mano (p. ej. folio de factura). Renombrado de
        // "Folio" a "Referencia" para no confundirlo con el folio operativo VO-… de arriba.
        { label: 'Referencia', value: detail.reference || 'Sin referencia' },
        { label: 'Registrado por', value: detail.createdByName || 'Sin usuario' },
        ...(detail.confirmedAt ? [{ label: 'Confirmado', value: `${formatSaleDate(detail.confirmedAt, timeZone)} por ${detail.confirmedByName || 'Sin usuario'}` }] : []),
      ]}
    />
    {/* Notas y anulación: a la vista, con la etiqueta encima (son textos largos). */}
    {(detail.notes || detail.cancelledAt || detail.cancellationReason) && <MetaList
      aria-label="Notas y anulación"
      items={[
        ...(detail.notes ? [{ label: 'Notas', value: detail.notes }] : []),
        ...(detail.cancelledAt ? [{ label: 'Anulado', value: `${formatSaleDate(detail.cancelledAt, timeZone)} por ${detail.cancelledByName || 'Sin usuario'}` }] : []),
        ...(detail.cancellationReason ? [{ label: 'Motivo de anulación', value: detail.cancellationReason }] : []),
      ]}
      stack
    />}
    {/* Historial: anular siempre dejó rastro; modificar no dejaba ninguno. Mismo bloque y mismo CSS
        que Gastos y Compras. Solo aparece si hubo cambios — un bloque vacío no le sirve a nadie. */}
    {detail.history && detail.history.length > 0 && <section className="change-history">
      <h4>Historial de cambios</h4>
      <ul>{detail.history.map((change, index) => <li key={index}>
        <div className="change-history__what">
          <strong>{change.fieldLabel}</strong>
          <span className="change-history__values">
            <span className="change-history__old">{change.oldValue ?? 'vacío'}</span>
            <span className="change-history__arrow" aria-label="cambió a"> → </span>
            <span className="change-history__new">{change.newValue ?? 'vacío'}</span>
          </span>
        </div>
        <small>{formatSaleDate(change.changedAt, timeZone)}{change.changedByName ? ` · ${change.changedByName}` : ''}</small>
      </li>)}</ul>
    </section>}

    {/* Fase 21C-4B: líneas de una venta del mini-POS (snapshots históricos). Una venta histórica o
        administrativa no trae líneas (lines ausente/vacío) y esta sección simplemente no se muestra. */}
    {detail.lines && detail.lines.length > 0 && <div className="other-sale-lines">
      <div className="stock-heading"><div><strong>Productos vendidos</strong><span>Precios e IVA tal como se registraron en la venta.</span></div></div>
      {/* P-076: en el teléfono, filas compactas (producto · cantidad × precio · total; subtotal e IVA en la línea de datos)
          en vez de una tabla de siete columnas que obligaba a desplazarse de lado. En tableta y escritorio, la tabla. */}
      {tier === 'compact' ? <DenseList label="Productos vendidos" plain>{detail.lines.map((line) => <DenseRow
        key={line.id}
        meta={[`Subtotal: ${formatMoney(line.subtotalAmount, detail.currency)}`, `IVA: ${formatMoney(line.taxAmount, detail.currency)}`]}
        subtitle={`${line.quantity} × ${formatMoney(line.unitPrice, detail.currency)} · ${formatResponseTaxMode(line.taxMode)}${line.taxMode === 'CUSTOM' && line.taxPercentage ? ` (${Number(line.taxPercentage).toFixed(1)}%)` : ''}`}
        title={line.productName}
        value={formatMoney(line.totalAmount, detail.currency)}
      />)}</DenseList> : <div className="data-table-wrap"><table className="data-table" style={{ minWidth: 0 }}><thead><tr>
        <th>Producto</th><th>Cant.</th><th>P. unit.</th><th>Subtotal</th><th>IVA</th><th>Total</th><th>Modo</th>
      </tr></thead><tbody>{detail.lines.map((line) => <tr key={line.id}>
        <td>{line.productName}</td>
        <td>{line.quantity}</td>
        <td>{formatMoney(line.unitPrice, detail.currency)}</td>
        <td>{formatMoney(line.subtotalAmount, detail.currency)}</td>
        <td>{formatMoney(line.taxAmount, detail.currency)}</td>
        <td><strong>{formatMoney(line.totalAmount, detail.currency)}</strong></td>
        <td>{formatResponseTaxMode(line.taxMode)}{line.taxMode === 'CUSTOM' && line.taxPercentage ? ` (${Number(line.taxPercentage).toFixed(1)}%)` : ''}</td>
      </tr>)}</tbody></table></div>}
    </div>}

    {/* Fase 22C-1: trazabilidad de inventario de solo lectura (snapshots históricos). Se carga bajo
        demanda; no aparece en el carrito ni permite editar/reparar. */}
    <InventoryTraceSection saleId={detail.id} />

    {/* Fase 22C-3: costeo histórico + margen — solo para roles con financials.viewCosts. Si el usuario
        no tiene permiso, la sección NO se renderiza y NO se solicita al backend (que también aplica 403). */}
    {canViewCosts(user?.role) && <CostingSection saleId={detail.id} />}

    {/* P-076: las mismas acciones, en el mismo sitio y con las mismas condiciones; lo destructivo (eliminar el
        borrador, anular) va aparte, en texto rojo. */}
    <ActionBar
      aria-label="Acciones de la venta"
      danger={(actions.canDeleteDraft || (actions.canCancel && canCancelOtherSale(user?.role))) ? <>
        {actions.canDeleteDraft && <Button onClick={() => void onDeleteDraft()} variant="danger">Eliminar borrador</Button>}
        {actions.canCancel && canCancelOtherSale(user?.role) && <Button onClick={() => setIsCancelModalOpen(true)} variant="danger">Anular venta</Button>}
      </> : undefined}
      note={detail.status === 'CANCELLED' ? 'Esta venta ya fue anulada.'
        : actions.canCancel && detail.hasPendingVoidRequest ? 'Ya solicitaste un cambio de esta venta — está pendiente de revisión.'
          : actions.canCancel && !canCancelOtherSale(user?.role) && (!canCreateVoidRequest(user?.role) || detail.createdByUserId !== user?.id) ? 'No tienes permiso para anular ventas.'
            : undefined}
    >
      {actions.canEdit && <Button onClick={onEdit} variant="secondary">Editar</Button>}
      {actions.canCancel && !canCancelOtherSale(user?.role) && canCreateVoidRequest(user?.role) && detail.createdByUserId === user?.id && !detail.hasPendingVoidRequest
        && <Button onClick={() => setIsVoidRequestModalOpen(true)} variant="secondary">Solicitar anulación o cambio</Button>}
    </ActionBar>

    {isCancelModalOpen && <CancelOtherSaleModal
      sale={detail}
      timeZone={timeZone}
      onClose={() => setIsCancelModalOpen(false)}
      onCancelled={() => { setIsCancelModalOpen(false); void onChanged('Venta anulada correctamente.'); }}
    />}

    {isVoidRequestModalOpen && <VoidRequestModal
      targetType="OTHER_SALE"
      targetId={detail.id}
      targetSummary={`Venta ${detail.folio ? `${detail.folio} ` : ''}— ${detail.concept} — ${formatMoney(detail.amount, detail.currency)}`}
      storeId={detail.storeId}
      occurredAt={detail.saleDate}
      timeZone={timeZone}
      onClose={() => setIsVoidRequestModalOpen(false)}
      onSubmitted={async (message) => { setIsVoidRequestModalOpen(false); await onChanged(message); }}
    />}
  </Modal>;
}

function InventoryTraceSection({ saleId }: { saleId: string }) {
  const [trace, setTrace] = useState<InventoryTrace>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setError(undefined);
    getOtherSaleInventoryTrace(saleId)
      .then((result) => { if (active) setTrace(result.inventoryTrace); })
      .catch((loadError) => { if (active) setError(getErrorMessage(loadError, 'No fue posible cargar la afectación de inventario.')); })
      .finally(() => { if (active) setIsLoading(false); });
    return () => { active = false; };
  }, [saleId]);

  return <div className="other-sale-lines">
    <div className="stock-heading"><div><strong>Afectación de inventario</strong><span>Trazabilidad de solo lectura, con los datos tal como se registraron en la venta.</span></div></div>
    {isLoading && <div className="inline-loading"><span className="spinner" />Cargando afectación de inventario...</div>}
    {!isLoading && error && <div className="alert alert--error">{error}</div>}
    {!isLoading && !error && trace && <InventoryTraceBody trace={trace} />}
  </div>;
}

function InventoryTraceBody({ trace }: { trace: InventoryTrace }) {
  const summary = traceSummary(trace.traceStatus);
  const showLines = trace.traceStatus !== 'NOT_APPLICABLE' && trace.lines.length > 0;
  return <div>
    <p><span className={`stock-badge stock-badge--${summary.tone}`}>{summary.label}</span></p>
    <p className="posting-blocked-note">{summary.hint}</p>
    {showLines && <div className="inventory-trace-lines">{trace.lines.map((line) => <InventoryTraceLine key={line.lineId} line={line} historical={trace.traceStatus === 'HISTORICAL_NO_TRACE'} />)}</div>}
  </div>;
}

function InventoryTraceLine({ line, historical }: { line: LineTrace; historical: boolean }) {
  const tier = useShellTier();
  // Fase 22C-2: nombre de la receta tal como se registró al vender (snapshot). Nunca la receta actual.
  const recipeLabel = line.recipeName ?? (line.hasRecipeReference ? 'Receta registrada' : null);
  return <div className="inventory-trace-line">
    <div className="inventory-trace-line__head">
      <strong>{line.quantity}× {line.productName}</strong>
      {recipeLabel && <span className="status-pill status-pill--neutral">Receta: {recipeLabel}</span>}
      {line.status === 'NO_RECIPE' && <span className="status-pill status-pill--neutral">Este producto no afecta inventario porque no tiene receta asignada.</span>}
      {line.status === 'HISTORICAL_NO_TRACE' && !historical && <span className="status-pill status-pill--neutral">Sin trazabilidad de inventario registrada.</span>}
    </div>
    {/* P-076: en el teléfono, cada insumo como fila compacta (insumo · receta × factor = descontado · estado); en tableta y escritorio, la tabla. */}
    {line.consumptions.length > 0 && (tier === 'compact' ? <DenseList label={`Insumos descontados de ${line.productName}`} plain>{line.consumptions.map((c) => <DenseRow
      badge={<span className={`stock-badge stock-badge--${consumptionStatusLabel(c.status).tone}`}>{consumptionStatusLabel(c.status).label}</span>}
      key={c.id}
      meta={[`Receta: ${c.recipeQuantity} ${c.recipeUnitSymbol}`, `Factor: × ${c.conversionFactor}`, ...(c.status === 'REVERSED' && c.reversal ? [`Restaurado ${new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium' }).format(new Date(c.reversal.occurredAt))}`] : [])]}
      title={c.ingredientName}
      value={`${c.inventoryQuantity} ${c.inventoryUnitSymbol}`}
    />)}</DenseList> : <div className="data-table-wrap"><table className="data-table" style={{ minWidth: 0 }}><thead><tr>
      <th>Insumo</th><th>Receta</th><th>Factor</th><th>Descontado</th><th>Estado</th>
    </tr></thead><tbody>{line.consumptions.map((c) => <tr key={c.id}>
      <td>{c.ingredientName}</td>
      <td>{c.recipeQuantity} {c.recipeUnitSymbol}</td>
      <td>× {c.conversionFactor}</td>
      <td>{c.inventoryQuantity} {c.inventoryUnitSymbol}</td>
      <td>
        <span className={`stock-badge stock-badge--${consumptionStatusLabel(c.status).tone}`}>{consumptionStatusLabel(c.status).label}</span>
        {c.status === 'REVERSED' && c.reversal && <small> · restaurado {new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium' }).format(new Date(c.reversal.occurredAt))}</small>}
      </td>
    </tr>)}</tbody></table></div>)}
  </div>;
}

function CostingSection({ saleId }: { saleId: string }) {
  const [costing, setCosting] = useState<SaleCosting>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();
  useEffect(() => {
    let active = true;
    setIsLoading(true); setError(undefined);
    getOtherSaleCosting(saleId)
      .then((result) => { if (active) setCosting(result.costing); })
      .catch((loadError) => { if (active) setError(getErrorMessage(loadError, 'No fue posible cargar el costeo.')); })
      .finally(() => { if (active) setIsLoading(false); });
    return () => { active = false; };
  }, [saleId]);
  return <div className="other-sale-lines">
    <div className="stock-heading"><div><strong>Costeo y margen</strong><span>Costo histórico congelado al confirmar la venta (no se recalcula con datos actuales).</span></div></div>
    {isLoading && <div className="inline-loading"><span className="spinner" />Cargando costeo...</div>}
    {!isLoading && error && <div className="alert alert--error">{error}</div>}
    {!isLoading && !error && costing && <CostingBody costing={costing} />}
  </div>;
}

function CostingBody({ costing }: { costing: SaleCosting }) {
  const summary = costSummary(costing.costQuality);
  const showLines = costing.costQuality !== 'NOT_APPLICABLE' && costing.lines.length > 0;
  // P-071: una venta anulada solo «restauró inventario» si alguna línea tenía consumos; una aproximada no tenía.
  const hadConsumptions = costing.lines.some((line) => line.consumptions.length > 0);
  return <div>
    <p><span className={`stock-badge stock-badge--${summary.tone}`}>{summary.label}</span>{costing.reversed && <span className="stock-badge stock-badge--entry" style={{ marginLeft: '0.4rem' }}>{hadConsumptions ? 'Anulada · inventario restaurado' : 'Anulada · sin inventario que restaurar'}</span>}</p>
    <p className="posting-blocked-note">{summary.hint}{costing.approximate && summary.margin !== 'approximate' ? ` ${APPROXIMATE_COST_NOTE}` : ''}</p>
    {/* P-076: lista etiqueta/valor; el margen es lo que manda. «(aprox.)» y «No disponible» se quedan en la etiqueta y
        el valor: la fiabilidad nunca se pliega. */}
    <MetaList
      aria-label="Costeo de la venta"
      columns
      items={[
        { label: 'Ingreso neto (antes de IVA)', value: formatCostMoney(costing.netRevenue, costing.currency) },
        // P-071: el total se declara aproximado siempre que alguna línea lo sea, también en una venta PARTIAL.
        { label: `Costo histórico${costing.approximate ? ' (aprox.)' : ''}`, value: formatCostMoney(costing.totalCost, costing.currency) },
        ...(summary.margin !== 'none'
          ? [
            // P-071: con costo aproximado el margen se muestra, dicho como estimación.
            { label: `Margen bruto${summary.margin === 'approximate' ? ' (aprox.)' : ''}`, value: formatCostMoney(costing.grossMargin, costing.currency), emphasis: true },
            { label: 'Margen %', value: costing.grossMarginPercentage !== null ? `${costing.grossMarginPercentage}%` : '—' },
          ]
          : [{ label: 'Margen bruto', value: 'No disponible (costo incompleto o sin definir)' }]),
      ]}
    />
    {showLines && <div className="inventory-trace-lines">{costing.lines.map((line) => <CostingLine key={line.lineId} line={line} currency={costing.currency} />)}</div>}
  </div>;
}

function CostingLine({ line, currency }: { line: LineCost; currency: string }) {
  const tier = useShellTier();
  const summary = costSummary(line.costQuality);
  return <div className="inventory-trace-line">
    <div className="inventory-trace-line__head">
      <strong>{line.quantity}× {line.productName}</strong>
      <span className={`stock-badge stock-badge--${summary.tone}`}>{summary.label}</span>
      <small>Ingreso {formatCostMoney(line.netRevenue, currency)}{line.totalCost ? ` · Costo ${formatCostMoney(line.totalCost, currency)}` : ''}{line.grossMargin && summary.margin !== 'none' ? ` · Margen ${formatCostMoney(line.grossMargin, currency)}` : ''}</small>
    </div>
    {/* P-076: en el teléfono, cada insumo como fila compacta (insumo · cantidad · costo unitario · costo total · estado); en tableta y escritorio, la tabla. */}
    {line.consumptions.length > 0 && (tier === 'compact' ? <DenseList label={`Costo de los insumos de ${line.productName}`} plain>{line.consumptions.map((c) => <DenseRow
      badge={<span className={`stock-badge stock-badge--${c.status === 'CALCULATED' ? 'out' : 'warn'}`}>{c.status === 'CALCULATED' ? 'Con costo' : 'Sin costo'}</span>}
      key={c.id}
      meta={[`Cantidad: ${c.inventoryQuantity} ${c.inventoryUnitSymbol}`, `Costo unit.: ${c.unitCost !== null ? `${c.unitCost} ${currency}` : '—'}`]}
      title={c.ingredientName}
      value={formatCostMoney(c.totalCost, currency)}
    />)}</DenseList> : <div className="data-table-wrap"><table className="data-table" style={{ minWidth: 0 }}><thead><tr>
      <th>Insumo</th><th>Cantidad</th><th>Costo unit.</th><th>Costo total</th><th>Estado</th>
    </tr></thead><tbody>{line.consumptions.map((c) => <tr key={c.id}>
      <td>{c.ingredientName}</td>
      <td>{c.inventoryQuantity} {c.inventoryUnitSymbol}</td>
      <td>{c.unitCost !== null ? `${c.unitCost} ${currency}` : '—'}</td>
      <td>{formatCostMoney(c.totalCost, currency)}</td>
      <td><span className={`stock-badge stock-badge--${c.status === 'CALCULATED' ? 'out' : 'warn'}`}>{c.status === 'CALCULATED' ? 'Con costo' : 'Sin costo'}</span></td>
    </tr>)}</tbody></table></div>)}
  </div>;
}

function CostingReportView({ stores }: { stores: Store[] }) {
  const tier = useShellTier();
  const today = dateToStoreISO(new Date());
  const monthAgo = dateToStoreISO(new Date(Date.now() - 30 * 86400000));
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);
  const [storeId, setStoreId] = useState('');
  const [report, setReport] = useState<CostingReport>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();

  const apply = async () => {
    setIsLoading(true); setError(undefined);
    try { setReport((await getOtherSaleCostingReport({ from, to, ...(storeId ? { storeId } : {}) })).report); }
    catch (e) { setError(getErrorMessage(e, 'No fue posible cargar el reporte.')); }
    finally { setIsLoading(false); }
  };
  useEffect(() => { void apply(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const s = report?.summary;
  return <div>
    <div className="stock-heading"><div><strong>Reporte de consumo y margen</strong><span>Costo histórico congelado; el margen solo se calcula sobre ventas con costo completo.</span></div></div>
    <form className="stock-filters" onSubmit={(e) => { e.preventDefault(); void apply(); }}>
      <DateRangeField from={from} to={to} onChange={(nextFrom, nextTo) => { setFrom(nextFrom); setTo(nextTo); }} />
      <label className="field"><span>Tienda</span><select value={storeId} onChange={(e) => setStoreId(e.target.value)}><option value="">Todas mis tiendas</option>{stores.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}</select></label>
      <button className="button button--primary" type="submit" disabled={isLoading}>{isLoading ? 'Cargando...' : 'Aplicar'}</button>
    </form>
    {error && <div className="alert alert--error">{error}</div>}
    {isLoading && <div className="inline-loading"><span className="spinner" />Cargando reporte...</div>}
    {!isLoading && s && <>
      {/* P-076: las cinco cifras del resultado primero (ingreso, costo, margen, margen % y la cobertura, que es la nota de
          confianza y nunca se pliega) y los dos conteos bajo «Ver más»; antes eran siete tarjetas iguales apiladas en el teléfono.
          Las ventas con margen negativo se avisan junto al margen cuando las hay. */}
      <StatSummary
        label="Resumen de costeo"
        more={[
          { label: 'Ventas vigentes', value: s.salesCount },
          { label: 'Ventas margen negativo', value: s.negativeMarginSales },
        ]}
        moreLabel="Ver ventas vigentes y con margen negativo"
        stats={[
          { label: 'Ingreso neto', value: formatCostMoney(s.netRevenue, 'MXN') },
          { label: 'Costo calculado', value: formatCostMoney(s.calculatedCost, 'MXN') },
          { hint: s.negativeMarginSales > 0 ? `${s.negativeMarginSales} con margen negativo` : undefined, label: 'Margen bruto (cubierto)', tone: s.negativeMarginSales > 0 ? 'warning' : 'default', value: formatCostMoney(s.calculatedMargin, 'MXN') },
          { label: 'Margen %', value: s.marginPct !== null ? `${s.marginPct}%` : '—' },
          { label: 'Cobertura de costo', value: s.coveragePct !== null ? `${s.coveragePct}%` : '—' },
        ]}
      />
      <p className="posting-blocked-note">El margen corresponde únicamente a las ventas con costo completo ({formatCostMoney(s.coveredRevenue, 'MXN')} de {formatCostMoney(s.netRevenue, 'MXN')}). Las canceladas se excluyen del resultado vigente.</p>
      {/* P-071: lo cubierto con costos aproximados se declara; nunca se presenta como exacto. */}
      {s.approximate?.note && <div className="alert alert--warning" role="status">{s.approximate.note} Ventas con costo aproximado: {s.approximate.sales} ({formatCostMoney(s.approximate.netRevenue, 'MXN')}{s.approximate.pctOfRevenue !== null ? `, ${s.approximate.pctOfRevenue}% del ingreso` : ''}).</div>}

      <div className="other-sale-lines"><div className="stock-heading"><div><strong>Calidad del costeo</strong></div></div>
        <div className="data-table-wrap"><table className="data-table n3-table--analytic" style={{ minWidth: 0 }}><thead><tr><th>Estado</th><th>Ventas</th><th>Líneas</th><th>Ingreso neto</th><th>% ingreso</th></tr></thead><tbody>
          {report!.coverage.map((c) => <tr key={c.quality}><td>{costSummary(c.quality).label}</td><td>{c.sales}</td><td>{c.lines}</td><td>{formatCostMoney(c.netRevenue, 'MXN')}</td><td>{c.pctOfRevenue !== null ? `${c.pctOfRevenue}%` : '—'}</td></tr>)}
        </tbody></table></div>
      </div>

      {report!.byProduct.length > 0 && <div className="other-sale-lines"><div className="stock-heading"><div><strong>Por producto</strong></div></div>
        <div className="data-table-wrap"><table className="data-table n3-table--analytic" style={{ minWidth: 0 }}><thead><tr><th>Producto</th><th>Cant.</th><th>Ingreso</th><th>Costo</th><th>Margen</th><th>Cobertura</th></tr></thead><tbody>
          {report!.byProduct.map((p) => <tr key={p.productId ?? p.productName}><td>{p.productName}</td><td>{p.quantity}</td><td>{formatCostMoney(p.netRevenue, 'MXN')}</td><td>{formatCostMoney(p.cost, 'MXN')}{p.approximate ? ' (aprox.)' : ''}</td><td>{p.margin !== null ? `${formatCostMoney(p.margin, 'MXN')}${p.approximate ? ' (aprox.)' : ''}` : <span className="status-pill status-pill--neutral">No disponible</span>}</td><td>{p.coverageComplete ? (p.approximate ? 'Completa (aprox.)' : 'Completa') : 'Incompleta'}</td></tr>)}
        </tbody></table></div>
      </div>}

      {report!.byIngredient.length > 0 && <div className="other-sale-lines"><div className="stock-heading"><div><strong>Por insumo</strong></div></div>
        <div className="data-table-wrap"><table className="data-table n3-table--analytic" style={{ minWidth: 0 }}><thead><tr><th>Insumo</th><th>Unidad</th><th>Cantidad</th><th>Costo</th><th>Cobertura</th></tr></thead><tbody>
          {report!.byIngredient.map((ing, i) => <tr key={`${ing.ingredientId}-${ing.unit}-${i}`}><td>{ing.ingredientName}</td><td>{ing.unit}</td><td>{ing.quantity}</td><td>{formatCostMoney(ing.cost, 'MXN')}{ing.unknownCostLines > 0 ? ' *' : ''}</td><td>{ing.coverageComplete ? 'Completa' : `Incompleta (${ing.unknownCostLines})`}</td></tr>)}
        </tbody></table></div>
      </div>}

      {report!.cancellations.sales > 0 && <div className="other-sale-lines"><div className="stock-heading"><div><strong>Cancelaciones</strong><span>Excluidas del resultado vigente.</span></div></div>
        <dl className="detail-list">
          <div><dt>Ventas canceladas</dt><dd>{report!.cancellations.sales}</dd></div>
          <div><dt>Costo original{report!.cancellations.approximate ? ' (incluye aprox.)' : ''}</dt><dd>{formatCostMoney(report!.cancellations.originalCost, 'MXN')}{report!.cancellations.approximate ? ` · aproximado ${formatCostMoney(report!.cancellations.approximateCost, 'MXN')}` : ''}</dd></div>
          <div><dt>Costo revertido</dt><dd>{formatCostMoney(report!.cancellations.reversedCost, 'MXN')}</dd></div>
          <div><dt>Reversas inconsistentes</dt><dd>{report!.cancellations.inconsistentReversals}</dd></div>
        </dl>
      </div>}

      {report!.issues.length > 0 && <div className="other-sale-lines"><div className="stock-heading"><div><strong>Hallazgos y revisión</strong><span>Diagnóstico de solo lectura; no corrige nada.</span></div></div>
        {/* P-076: en el teléfono, cada hallazgo como fila compacta (folio · etiqueta · detalle completo); la tabla cortaba el detalle. En tableta y escritorio, la tabla. */}
        {tier === 'compact' ? <DenseList label="Hallazgos y revisión" plain>{report!.issues.map((iss, i) => <DenseRow
          badge={<span className="stock-badge stock-badge--warn">{iss.label}</span>}
          key={i}
          subtitle={iss.detail}
          title={displayFolio(iss.folio)}
        />)}</DenseList> : <div className="data-table-wrap"><table className="data-table" style={{ minWidth: 0 }}><thead><tr><th>Folio</th><th>Etiqueta</th><th>Detalle</th></tr></thead><tbody>
          {report!.issues.map((iss, i) => <tr key={i}><td>{displayFolio(iss.folio)}</td><td><span className="stock-badge stock-badge--warn">{iss.label}</span></td><td>{iss.detail}</td></tr>)}
        </tbody></table></div>}
      </div>}
    </>}

    <CurrentCostEstimator stores={stores} />
  </div>;
}

function CurrentCostEstimator({ stores }: { stores: Store[] }) {
  const tier = useShellTier();
  const [storeId, setStoreId] = useState('');
  const [products, setProducts] = useState<OtherSaleProduct[]>([]);
  const [productId, setProductId] = useState('');
  const [result, setResult] = useState<CurrentProductCost>();
  const [error, setError] = useState<string>();
  useEffect(() => { void listOtherSaleProducts({ includeInactive: true }).then((r) => setProducts(r.items)).catch(() => setProducts([])); }, []);

  const estimate = async () => {
    setError(undefined); setResult(undefined);
    if (!storeId || !productId) { setError('Selecciona tienda y producto.'); return; }
    try { setResult((await getCurrentProductCost({ productId, storeId })).currentCost); }
    catch (e) { setError(getErrorMessage(e, 'No fue posible estimar el costo.')); }
  };
  return <div className="other-sale-lines">
    <div className="stock-heading"><div><strong>Costo estimado actual</strong><span>Estimación con la receta y los costos actuales. No modifica ventas anteriores.</span></div></div>
    <form className="stock-filters" onSubmit={(e) => { e.preventDefault(); void estimate(); }}>
      <label className="field"><span>Tienda</span><select value={storeId} onChange={(e) => setStoreId(e.target.value)}><option value="">Selecciona...</option>{stores.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}</select></label>
      <label className="field"><span>Producto</span><select value={productId} onChange={(e) => setProductId(e.target.value)}><option value="">Selecciona...</option>{products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>
      <button className="button button--primary" type="submit">Estimar</button>
    </form>
    {error && <div className="alert alert--error">{error}</div>}
    {result && <div>
      <p><span className={`stock-badge stock-badge--${costSummary(currentCostQuality(result.status)).tone}`}>{result.status}</span>{result.statusDetail ? ` · ${result.statusDetail}` : ''}</p>
      <dl className="detail-list">
        <div><dt>Ingreso neto estimado</dt><dd>{formatCostMoney(result.netRevenue, result.currency)}</dd></div>
        <div><dt>Costo estimado</dt><dd>{formatCostMoney(result.totalCost, result.currency)}</dd></div>
        <div><dt>Margen estimado</dt><dd>{result.grossMargin !== null && costSummary(currentCostQuality(result.status)).margin !== 'none' ? `${formatCostMoney(result.grossMargin, result.currency)}${result.grossMarginPercentage !== null ? ` (${result.grossMarginPercentage}%)` : ''}` : 'No disponible'}</dd></div>
      </dl>
      {/* P-076: en el teléfono, cada insumo como fila compacta (insumo · cantidad · costo unitario actual · costo); en tableta y escritorio, la tabla. */}
      {result.components.length > 0 && (tier === 'compact' ? <DenseList label="Insumos del costo estimado" plain>{result.components.map((c, i) => <DenseRow
        key={i}
        meta={[`Cantidad: ${c.inventoryQuantity} ${c.inventoryUnitSymbol}`, `Costo unit. actual: ${c.unitCost !== null ? `${c.unitCost} ${result.currency}` : '—'}`]}
        title={c.ingredientName}
        value={formatCostMoney(c.componentCost, result.currency)}
      />)}</DenseList> : <div className="data-table-wrap"><table className="data-table" style={{ minWidth: 0 }}><thead><tr><th>Insumo</th><th>Cantidad</th><th>Costo unit. actual</th><th>Costo</th></tr></thead><tbody>
        {result.components.map((c, i) => <tr key={i}><td>{c.ingredientName}</td><td>{c.inventoryQuantity} {c.inventoryUnitSymbol}</td><td>{c.unitCost !== null ? `${c.unitCost} ${result.currency}` : '—'}</td><td>{formatCostMoney(c.componentCost, result.currency)}</td></tr>)}
      </tbody></table></div>)}
    </div>}
  </div>;
}

function CancelOtherSaleModal({ sale, timeZone, onClose, onCancelled }: { sale: OtherSaleDetail; timeZone: string; onClose: () => void; onCancelled: () => void }) {
  const [reason, setReason] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();

  const canConfirm = reason.trim().length >= MIN_CANCEL_REASON_LENGTH;

  const confirmCancel = async () => {
    if (!canConfirm || isSaving) return;
    setIsSaving(true);
    setError(undefined);
    try {
      await cancelOtherSale(sale.id, { confirm: true, reason: reason.trim() });
      onCancelled();
    } catch (cancelError) {
      setError(getErrorMessage(cancelError, 'No fue posible anular esta venta.'));
    } finally {
      setIsSaving(false);
    }
  };

  return <Modal
    title="Anular venta"
    isBusy={isSaving}
    onClose={onClose}
    footer={<>
      <button className="button button--ghost" disabled={isSaving} onClick={onClose} type="button">Cancelar</button>
      <button className="button button--danger" disabled={isSaving || !canConfirm} onClick={() => void confirmCancel()} type="button">{isSaving ? 'Anulando...' : 'Anular venta'}</button>
    </>}
  >
    {error && <div className="alert alert--error">{error}</div>}
    {/* Si esta venta ya la contó un corte cerrado, decirlo ANTES de anular — no después. */}
    <ClosedShiftNotice storeId={sale.storeId} at={sale.saleDate} timeZone={timeZone} action="anular" />
    <dl className="detail-list">
      <div><dt>Tienda</dt><dd>{sale.storeName || 'Ingreso general'}</dd></div>
      <div><dt>Concepto</dt><dd>{sale.concept}</dd></div>
      <div><dt>Monto</dt><dd>{formatMoney(sale.amount, sale.currency)}</dd></div>
    </dl>
    <div className="alert alert--warning">Esta venta no se borrará del historial. Se marcará como anulada y dejará de contarse como ingreso activo.</div>
    <label className="field">
      <span>Motivo de la anulación (mínimo {MIN_CANCEL_REASON_LENGTH} caracteres)</span>
      <textarea onChange={(event) => setReason(event.target.value)} rows={3} value={reason} />
    </label>
  </Modal>;
}

function OtherSaleFormModal({ saleId, stores, onClose, onSaved }: { saleId: string | undefined; stores: Store[]; onClose: () => void; onSaved: (message: string) => Promise<void> }) {
  const { user } = useAuth();
  const loyverseConnected = useLoyversePresence();
  const isEditing = saleId !== undefined;
  // Fase 21A: un EMPLOYEE solo tiene su propia tienda asignada en `stores` (el backend ya filtra
  // la lista) — se preselecciona y se bloquea, nunca "ingreso general" ni otra tienda.
  const isEmployee = user?.role === 'EMPLOYEE';
  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();
  // FI-22 · Entrega 1: nada preseleccionado. Categoría, IVA, ¿facturada? y método de pago se eligen a
  // propósito (tarjetas con círculo); la fecha es explícita (Hoy pide la hora con «Ahora»).
  const [form, setForm] = useState({
    storeId: isEmployee ? stores[0]?.id || '' : '', category: '' as OtherSaleCategory | '', subcategoryId: '',
    concept: '', amount: '', taxMode: '' as OtherSaleTaxMode | '', taxPercentage: '', isInvoiced: '' as '' | 'yes' | 'no',
    paymentMethod: '' as OtherSalePaymentMethod | '', customerName: '', reference: '', notes: '',
  });
  const [dateIso, setDateIso] = useState('');
  const [dateReady, setDateReady] = useState(false);
  const [loadedDate, setLoadedDate] = useState<string>();
  const [subcategories, setSubcategories] = useState<OtherSaleSubcategory[]>();
  const [isSubcategoryModalOpen, setIsSubcategoryModalOpen] = useState(false);

  const loadSubcategories = useCallback((category: OtherSaleCategory) => {
    setSubcategories(undefined);
    void getOtherSaleSubcategories(category).then((result) => setSubcategories(result.items)).catch(() => setSubcategories([]));
  }, []);
  useEffect(() => { if (form.category) loadSubcategories(form.category); else setSubcategories([]); }, [form.category, loadSubcategories]);

  useEffect(() => {
    if (!saleId) return;
    setIsLoading(true);
    void getOtherSale(saleId).then((result) => {
      const sale = result.otherSale;
      setForm({
        storeId: sale.storeId || '', category: sale.category, subcategoryId: sale.subcategoryId || '',
        // Fase 21C-4A: 'MIXED' solo existe en ventas CONFIRMED del mini-POS (no editables), así que
        // este formulario nunca lo recibe en la práctica; se coacciona a 'NONE' por seguridad de tipos.
        concept: sale.concept, amount: sale.amount, taxMode: sale.taxMode === 'MIXED' ? 'NONE' : sale.taxMode, taxPercentage: sale.taxMode === 'CUSTOM' ? (sale.taxPercentage || '') : '',
        isInvoiced: sale.isInvoiced ? 'yes' : 'no', paymentMethod: sale.paymentMethod || '',
        customerName: sale.customerName || '', reference: sale.reference || '', notes: sale.notes || '',
      });
      setLoadedDate(sale.saleDate);
    }).catch((loadError) => setError(getErrorMessage(loadError, 'No fue posible cargar la venta.'))).finally(() => setIsLoading(false));
  }, [saleId]);

  const taxBreakdown = form.taxMode ? calculateOtherSaleTaxBreakdown(Number(form.amount), form.taxMode, form.taxPercentage === '' ? null : Number(form.taxPercentage)) : null;

  const buildPayload = (): CreateOtherSaleInput => ({
    storeId: form.storeId || null,
    saleDate: dateIso,
    category: form.category as OtherSaleCategory,
    subcategoryId: form.subcategoryId || null,
    concept: form.concept.trim(),
    amount: Number(form.amount),
    taxMode: form.taxMode as OtherSaleTaxMode,
    taxPercentage: form.taxMode === 'CUSTOM' ? Number(form.taxPercentage) : null,
    isInvoiced: form.isInvoiced === 'yes',
    paymentMethod: form.paymentMethod || null,
    customerName: form.customerName.trim() || null,
    reference: form.reference.trim() || null,
    notes: form.notes.trim() || null,
  });

  const save = async (confirm: boolean) => {
    if (isSaving) return;
    setIsSaving(true);
    setError(undefined);
    try {
      if (isEditing) {
        await updateOtherSale(saleId, buildPayload());
        await onSaved('Venta actualizada.');
      } else {
        await createOtherSale({ ...buildPayload(), confirm });
        await onSaved(confirm ? 'Venta guardada y confirmada.' : 'Venta guardada como borrador.');
      }
    } catch (saveError) {
      setError(getErrorMessage(saveError, 'No fue posible guardar la venta.'));
    } finally {
      setIsSaving(false);
    }
  };

  const missing = !form.category ? 'Elige el tipo de ingreso.' : form.concept.trim() === '' ? 'Escribe el concepto.' : !(Number(form.amount) > 0) ? 'Escribe el monto.' : !form.taxMode ? 'Elige cómo va el IVA.' : form.taxMode === 'CUSTOM' && form.taxPercentage === '' ? 'Escribe el porcentaje de IVA.' : !dateReady || !dateIso ? 'Elige la fecha y la hora.' : !form.paymentMethod ? 'Elige cómo pagaron.' : !form.isInvoiced ? 'Di si se facturó.' : null;
  const isValid = missing === null;
  const blur = (event: { key: string; preventDefault: () => void; currentTarget: HTMLElement }) => { if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur(); } };

  // P-075: el registro manual se llama por lo que es (el botón, el título y la línea de abajo dicen lo
  // mismo), para que nadie lo confunda con vender: no lleva productos ni descuenta inventario.
  return <Modal
    title={isEditing ? 'Editar ingreso manual' : 'Registrar ingreso manual'}
    description="Captura un importe sin productos; no descuenta inventario. Para vender con productos, usa Vender."
    isBusy={isSaving}
    onClose={onClose}
    // P-078 (ActionBar v2 · H): mismo pie, por intención. Quien no confirma (o edita) no tiene primaria: Guardar borrador
    // es su única acción de guardado y se queda como estaba.
    footer={<ActionBar aria-label="Acciones del ingreso" note={!isLoading && missing ? <FooterReason>{missing}</FooterReason> : undefined} primary={!isEditing && canConfirmOtherSale(user?.role) ? <button className="button button--primary" disabled={isSaving || !isValid} onClick={() => void save(true)} type="button">Guardar y confirmar</button> : undefined}>
      <button className="text-button" disabled={isSaving} onClick={onClose} type="button">Cancelar</button>
      <button className="text-button" disabled={isSaving || !isValid} onClick={() => void save(false)} type="button">{isSaving ? 'Guardando...' : 'Guardar borrador'}</button>
    </ActionBar>}
  >
    {error && <div className="alert alert--error">{error}</div>}
    {loyverseConnected === true && <div className="alert alert--warning">Registra aquí solo ingresos que no estén en Loyverse, para evitar duplicar ventas.</div>}
    {isLoading ? <div className="inline-loading"><span className="spinner" />Cargando...</div> : <>
      {isEmployee
        ? <Answers><label className="field"><span>Tienda</span><input disabled type="text" value={stores[0]?.name || 'Sin tienda asignada'} /><small>Este registro se guardará en tu tienda asignada.</small></label></Answers>
        : <><Question>¿En qué tienda?</Question><Answers><label className="field"><select aria-label="Tienda" onChange={(event) => setForm({ ...form, storeId: event.target.value })} value={form.storeId}><option value="">Ingreso general (sin tienda)</option>{stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}</select></label></Answers></>}
      <Question>¿Qué tipo de ingreso es?</Question>
      <ChoiceCards ariaLabel="Tipo de ingreso" onChange={(value) => setForm({ ...form, category: value, subcategoryId: '' })} options={CATEGORY_OPTIONS.map(([value, label]) => ({ value, label }))} value={form.category} variant="chips" />
      {form.category && <Answers>
        <div className="field field--with-action">
          <label className="field"><span>Subcategoría (opcional)</span><select onChange={(event) => setForm({ ...form, subcategoryId: event.target.value })} value={form.subcategoryId}>
            <option value="">{subcategories === undefined ? 'Cargando...' : 'Sin subcategoría'}</option>
            {subcategories?.map((subcategory) => <option key={subcategory.id} value={subcategory.id}>{subcategory.name}</option>)}
          </select></label>
          <button className="button button--ghost" onClick={() => setIsSubcategoryModalOpen(true)} type="button">+ Agregar</button>
        </div>
      </Answers>}
      <Question>¿Qué fue y cuánto?</Question>
      <Answers>
        <label className="field"><span>Concepto</span><input maxLength={200} onChange={(event) => setForm({ ...form, concept: event.target.value })} onKeyDown={blur} placeholder="Ej. Evento de fin de año" value={form.concept} /></label>
        <label className="field"><span>Monto (MXN){form.taxMode === 'CUSTOM' ? ' · subtotal sin IVA' : ''}</span><input enterKeyHint="done" inputMode="decimal" min="0" onChange={(event) => setForm({ ...form, amount: event.target.value })} onKeyDown={blur} placeholder="0.00" step="any" type="number" value={form.amount} /></label>
      </Answers>
      <Question>¿Cómo va el IVA?</Question>
      <ChoiceCards ariaLabel="IVA" onChange={(value) => setForm({ ...form, taxMode: value, taxPercentage: value === 'CUSTOM' ? form.taxPercentage : '' })} options={TAX_MODE_OPTIONS.map(([value, label]) => ({ value, label, hint: value === 'NONE' ? 'El monto es el total, sin impuesto' : value === 'INCLUDED' ? 'El monto ya trae el IVA' : 'El monto es el subtotal; se suma un porcentaje' }))} value={form.taxMode} />
      {form.taxMode === 'CUSTOM' && <Answers><label className="field"><span>Porcentaje de IVA</span><input enterKeyHint="done" inputMode="decimal" min="0" onChange={(event) => setForm({ ...form, taxPercentage: event.target.value })} onKeyDown={blur} placeholder="16" step="any" type="number" value={form.taxPercentage} /></label></Answers>}
      {taxBreakdown && Number(form.amount) > 0 && <div aria-label="Desglose de IVA" className="wiz-breakdown"><article><span>Subtotal</span><strong>{formatMoney(taxBreakdown.subtotalAmount.toFixed(2))}</strong></article><article><span>IVA ({(taxBreakdown.taxPercentage ?? 0).toFixed(1)}%)</span><strong>{formatMoney(taxBreakdown.taxAmount.toFixed(2))}</strong></article><article><span>Total</span><strong>{formatMoney(taxBreakdown.totalAmount.toFixed(2))}</strong></article></div>}
      <Question>¿Cuándo fue?</Question>
      <Answers><DateTimeField explicit label="" value={loadedDate} startAcknowledged={!!saleId} onChange={(iso, ready) => { setDateIso(iso); setDateReady(ready); }} /></Answers>
      <Question>¿Cómo pagaron?</Question>
      <ChoiceCards ariaLabel="Método de pago" onChange={(value) => setForm({ ...form, paymentMethod: value })} options={PAYMENT_METHOD_OPTIONS.map(([value, label]) => ({ value, label }))} value={form.paymentMethod} />
      <Question>¿Se facturó?</Question>
      <ChoiceCards ariaLabel="Facturada" onChange={(value) => setForm({ ...form, isInvoiced: value })} options={[{ value: 'yes' as const, label: 'Sí' }, { value: 'no' as const, label: 'No' }]} value={form.isInvoiced} />
      <Answers>
        <label className="field"><span>Referencia (opcional)</span><input maxLength={200} onChange={(event) => setForm({ ...form, reference: event.target.value })} onKeyDown={blur} placeholder="P. ej. folio de factura" value={form.reference} /></label>
        <label className="field"><span>Cliente (opcional)</span><input maxLength={200} onChange={(event) => setForm({ ...form, customerName: event.target.value })} onKeyDown={blur} placeholder="Opcional" value={form.customerName} /></label>
        <label className="field"><span>Notas (opcional)</span><textarea onChange={(event) => setForm({ ...form, notes: event.target.value })} placeholder="Opcional" rows={2} value={form.notes} /></label>
      </Answers>
    </>}
    {isSubcategoryModalOpen && form.category && <OtherSaleSubcategoryModal
      category={form.category}
      onClose={() => setIsSubcategoryModalOpen(false)}
      onCreated={(subcategory) => { setForm((current) => ({ ...current, subcategoryId: subcategory.id })); setIsSubcategoryModalOpen(false); if (form.category) loadSubcategories(form.category); }}
    />}
  </Modal>;
}

function OtherSaleSubcategoryModal({ category, onClose, onCreated }: { category: OtherSaleCategory; onClose: () => void; onCreated: (subcategory: OtherSaleSubcategory) => void }) {
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed || isSaving) return;
    setIsSaving(true);
    setError(undefined);
    try {
      const result = await createOtherSaleSubcategory({ category, name: trimmed });
      onCreated(result.subcategory);
    } catch (saveError) {
      setError(getErrorMessage(saveError, 'No fue posible registrar la subcategoría.'));
    } finally {
      setIsSaving(false);
    }
  };

  return <Modal
    title="Agregar subcategoría"
    isBusy={isSaving}
    onClose={onClose}
    footer={<>
      <button className="button button--ghost" disabled={isSaving} onClick={onClose} type="button">Cancelar</button>
      <button className="button button--primary" disabled={isSaving || !name.trim()} onClick={() => void save()} type="button">{isSaving ? 'Guardando...' : 'Aceptar'}</button>
    </>}
  >
    {error && <div className="alert alert--error">{error}</div>}
    <label className="field"><span>Nombre</span><input autoFocus onChange={(event) => setName(event.target.value)} placeholder="Ej. Boda, Fiesta infantil..." value={name} /></label>
  </Modal>;
}

function OtherSaleStatusPill({ status }: { status: OtherSaleStatus }) {
  const tone = status === 'CONFIRMED' ? 'applied' : status === 'CANCELLED' ? 'reverted' : 'pending';
  return <span className={`consumption-status consumption-status--${tone}`}>{OTHER_SALE_STATUS_LABELS[status]}</span>;
}

function formatSaleDate(value: string, timeZone: string = DEFAULT_STORE_TIMEZONE) {
  return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(new Date(value));
}

